import Button from "@/lib/Button";
import CheckIcon from "~icons/fa6-solid/check";
import XIcon from "~icons/fa6-solid/x";
import SettingsSectionLayout from "@/lib/SettingsSectionLayout";
import $api from "@/lib/queries/openapi";
import toast from "react-hot-toast";
import Select from "react-select";
import { useParams } from "react-router";
import { useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

export default function QuickBooksOnline() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();

  const {
    data: instanceData,
    error: instanceError,
    isLoading: instanceLoading,
  } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/quickbooks_online/instance",
    {
      params: { path: { team_slug: teamSlug! } },
      retry: false, // don't retry on 404
    },
  );

  const {
    data: accountsData,
    isLoading: loadingAccounts,
    error: accountsError,
  } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/quickbooks_online/accounts",
    {
      params: { path: { team_slug: teamSlug! } },
    },
    {
      enabled: !!instanceData && !instanceData.reconnect_required,
    },
  );

  useEffect(() => {
    if (accountsError) {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/quickbooks_online/instance"],
      });
    }
  }, [accountsError, queryClient]);

  const accountOptions = useMemo(() => {
    if (!accountsData) return [];
    return accountsData.map((a) => ({
      label: a.name,
      value: a.id,
    }));
  }, [accountsData]);

  const selectedAccount = useMemo(() => {
    const id = instanceData?.income_account_id;
    if (!id || !accountOptions.length) return null;
    return accountOptions.find((opt) => opt.value === id) ?? null;
  }, [instanceData, accountOptions]);

  const { mutate: connectToQuickBooks } = $api.useMutation(
    "get",
    "/api/teams/{team_slug}/quickbooks_online/oauth/request",
    {
      onSuccess: (data) => {
        if (data?.link) window.location.href = data.link;
        else toast.error("No link returned from server");
      },
      onError: () => toast.error("Failed to connect to QuickBooks Online"),
    },
  );

  const { mutate: disconnectFromQuickBooks } = $api.useMutation(
    "delete",
    "/api/teams/{team_slug}/quickbooks_online/instance",
    {
      onSuccess: () => {
        toast.success("Disconnected from QuickBooks Online");
        queryClient.invalidateQueries({
          queryKey: [
            "get",
            "/api/teams/{team_slug}/quickbooks_online/instance",
          ],
        });
      },
      onError: () => toast.error("Failed to disconnect from QuickBooks Online"),
    },
  );

  const { mutate: saveIncomeAccount } = $api.useMutation(
    "patch",
    "/api/teams/{team_slug}/quickbooks_online/instance",
    {
      onSuccess: () => {
        toast.success("Account saved");
        queryClient.invalidateQueries({
          queryKey: [
            "get",
            "/api/teams/{team_slug}/quickbooks_online/instance",
          ],
        });
      },
      onError: () => toast.error("Failed to save account"),
    },
  );

  return (
    <SettingsSectionLayout title="QuickBooks Online">
      <div className="flex flex-col gap-8 text-sm items-start">
        <div className="flex flex-col gap-2">
          <div className="font-bold">QuickBooks Online</div>
          <div className="text-gray-500">
            Connect your QuickBooks Online account to sync your quotes.
          </div>
        </div>
        {instanceLoading ? null : (
          <div className="flex flex-col gap-8 text-sm items-start">
            <div className="flex flex-col gap-2 mt-4">
              <div className="font-bold">Status</div>
              <div className="text-sm text-gray-700">
                {instanceError ? (
                  <div>Not Connected</div>
                ) : instanceData?.reconnect_required ? (
                  <div className="flex gap-4 items-center text-red-600">
                    <XIcon className="size-3" />
                    <div>
                      Your QuickBooks connection has expired or been revoked.
                      Please reconnect to continue
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4 items-center">
                    <CheckIcon className="text-blue-500" />
                    <div>
                      Connected to {instanceData?.company_name} since{" "}
                      {new Date(
                        instanceData?.created_at ?? "",
                      ).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                className="border"
                onClick={() =>
                  connectToQuickBooks({
                    params: { path: { team_slug: teamSlug! } },
                  })
                }
              >
                {instanceError ? "Connect to QuickBooks Online" : "Reconnect"}
              </Button>

              {!instanceError && !instanceData?.reconnect_required && (
                <Button
                  className="border border-red-500"
                  onClick={() =>
                    disconnectFromQuickBooks({
                      params: { path: { team_slug: teamSlug! } },
                    })
                  }
                >
                  Disconnect
                </Button>
              )}
            </div>

            {!instanceError && !instanceData?.reconnect_required && (
              <>
                <div className="flex flex-col gap-2">
                  <div className="font-bold">Settings</div>
                  <div className="text-sm">Account for new Items:</div>
                  <Select
                    placeholder="Select Account"
                    options={accountOptions}
                    isLoading={loadingAccounts}
                    value={selectedAccount}
                    onChange={(selected) => {
                      if (!selected) return;
                      saveIncomeAccount({
                        params: { path: { team_slug: teamSlug! } },
                        body: {
                          income_account_id: selected.value,
                        },
                      });
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </SettingsSectionLayout>
  );
}
